// src/components/ScrollTracker.tsx

"use client"

import { useEffect, type ReactNode } from "react"
import { usePathname } from "next/navigation"

import { throttle } from "~/lib/throttle"
import { trackScrollDepth, createEngagementStopwatch } from "~/lib/gtag"

/** Depth milestones (percent) reported once each, per page. */
const MILESTONES = [25, 50, 75, 100] as const

/**
 * Reports scroll-depth milestones (25/50/75/100%) once per page.
 *
 * This component is mounted once in the root layout (above the route), so it
 * does NOT remount on client-side navigation. It therefore resets its state on
 * every `pathname` change — the previous implementation kept `maxScrollDepth`
 * and a module-level stopwatch alive across navigations, so after scrolling
 * deep on one page no further milestones ever fired on subsequent pages.
 *
 * Milestones use threshold-CROSSING (`pct >= milestone`), not an exact
 * `pct % 25 === 0` match, so a fast/jumpy scroll that skips the exact integer
 * still records the milestone. A zero-height guard avoids divide-by-zero on
 * short pages.
 */
const ScrollTracker = (): ReactNode => {
  const pathname = usePathname()

  useEffect(() => {
    // Per-route state: a fresh milestone set + engagement stopwatch each page.
    const fired = new Set<number>()
    const getDelta = createEngagementStopwatch()

    const handleScroll = throttle(() => {
      const doc = document.documentElement
      const scrollable = doc.scrollHeight - doc.clientHeight
      if (scrollable <= 0) return // short page: nothing to scroll
      const pct = Math.min(100, Math.round((window.scrollY / scrollable) * 100))
      // Read the engagement delta at most ONCE per invocation: a single fast
      // scroll can cross several milestones, and the stopwatch resets on each
      // read — calling it per-milestone would report ~0ms for all but the first.
      let deltaMs: number | null = null
      for (const milestone of MILESTONES) {
        if (pct >= milestone && !fired.has(milestone)) {
          fired.add(milestone)
          deltaMs ??= getDelta()
          trackScrollDepth(milestone, deltaMs)
        }
      }
    }, 200)

    // Passive listener to avoid blocking scroll.
    window.addEventListener("scroll", handleScroll, { passive: true })
    // Cover pages that load already scrolled (e.g. anchor-link landings).
    handleScroll()

    return () => {
      window.removeEventListener("scroll", handleScroll)
      // cancel (not flush) on cleanup: a pending trailing call would re-read the
      // CURRENT DOM, which on a client-side route change is already the NEXT
      // page (scrollY ~ 0) — flushing would mis-attribute a scroll_depth to the
      // wrong page_path. Milestones are recorded on the leading edge the moment
      // they're crossed, so cancelling at most drops a same-page re-fire reached
      // within the 200ms window, which is the safe tradeoff vs mis-attribution.
      handleScroll.cancel()
    }
  }, [pathname])

  return null
}

export default ScrollTracker
